// src/components/LocalHandleList.tsx
// Pre-signup local list of the user's own social handles — no auth, no backend,
// no server calls. Mirrors LocalCrushList storage / add / remove mechanics.
//
// On-signup conversion (3b) reads readPresignupHandleStore() and calls
// attachContactPoint + confirmUserHandle for each confirmed entry.
//
// Storage key: "crushhh_presignup_handles"  (separate from crush key)
// Entry shape: { platform, handle (normalized, no @, no URL), addedAt, confirmed, imported? }
//
// TRUST BOUNDARY: public/unauth — no tokens, no mutations, no sensitive reads.

import { useState, useEffect, useRef } from "react";
import { FaInstagram, FaFacebook, FaSnapchatGhost } from "react-icons/fa";
import { SiTiktok } from "react-icons/si";
import { HandleHelpModal } from "./HandleHelpModal";

// ── Storage ────────────────────────────────────────────────────────────────────

export const HANDLES_LS_KEY = "crushhh_presignup_handles";

export interface PresignupHandle {
  platform: string;       // "instagram" | "tiktok" | "snapchat" | "facebook"
  handle: string;         // normalized: no @, no URL — plain username
  addedAt: number;
  confirmed: boolean;     // user clicked "This Is Me ✓" (= LocalCrushList.verified)
  imported?: boolean;     // set by 3b after conversion into authenticated vault
  contactPointId?: string; // written by 3b after successful attachContactPoint step
}

export interface PresignupHandleStore {
  version: 1;
  entries: PresignupHandle[];
}

export function readPresignupHandleStore(): PresignupHandle[] {
  try {
    const raw = localStorage.getItem(HANDLES_LS_KEY);
    if (!raw) return [];
    const parsed: PresignupHandleStore = JSON.parse(raw);
    if (parsed.version !== 1 || !Array.isArray(parsed.entries)) return [];
    return parsed.entries;
  } catch {
    return [];
  }
}

function writePresignupHandleStore(entries: PresignupHandle[]) {
  try {
    const store: PresignupHandleStore = { version: 1, entries };
    localStorage.setItem(HANDLES_LS_KEY, JSON.stringify(store));
  } catch {
    // storage quota full — non-fatal
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────────

// Normalize: strip @, lowercase, extract last path segment from URL.
// Mirrors LocalCrushList.normalizeSocialHandle (last-segment strategy).
function normalizeSocialHandle(input: string): string {
  let h = input.trim().toLowerCase().replace(/^@+/, "");
  if (h.includes("/")) {
    try {
      const url = new URL(h.startsWith("http") ? h : `https://${h}`);
      const parts = url.pathname.split("/").filter(Boolean);
      h = parts[parts.length - 1] || h;
    } catch {
      h = h.split("/").filter(Boolean).pop() || h;
    }
  }
  h = h.split("?")[0].split("#")[0];
  return h;
}

// Profile URL built at render/open time — never stored in the entry.
function profileUrl(platform: string, handle: string): string {
  switch (platform) {
    case "instagram": return `https://instagram.com/${handle}`;
    case "tiktok":    return `https://www.tiktok.com/@${handle}`;
    case "snapchat":  return `https://snapchat.com/add/${handle}`;
    case "facebook":  return `https://facebook.com/${handle}`;
    default: return "";
  }
}

// Unique session-state key for a given entry (used by viewedInTab / skipViewFor).
const entryKey = (e: PresignupHandle) => `${e.platform}:${e.handle}`;

// ── Constants ──────────────────────────────────────────────────────────────────

const CAP = 5; // one per platform is the practical maximum

const SOCIAL_PLATFORMS = [
  { id: "instagram", label: "Instagram", Icon: FaInstagram },
  { id: "tiktok",    label: "TikTok",    Icon: SiTiktok },
  { id: "snapchat",  label: "Snapchat",  Icon: FaSnapchatGhost },
  { id: "facebook",  label: "Facebook",  Icon: FaFacebook },
] as const;

const BRAND = {
  coral:  "#E05A3A",
  cream:  "#F0DFC8",
  ink:    "#1A1A1A",
  pageBg: "#FDF6EE",
  white:  "#FFFFFF",
} as const;

// ── Props ──────────────────────────────────────────────────────────────────────

interface Props {
  onHandleCountChange?: (count: number) => void;
  onConfirmedHandleCountChange?: (n: number) => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function LocalHandleList({ onHandleCountChange, onConfirmedHandleCountChange }: Props) {
  const [entries, setEntries] = useState<PresignupHandle[]>(() => readPresignupHandleStore());
  const [expanded,     setExpanded]     = useState(false);
  const [platform,     setPlatform]     = useState<string | null>(null);
  const [handleInput,  setHandleInput]  = useState("");
  const [error,        setError]        = useState<string | null>(null);
  const [addHov,       setAddHov]       = useState(false);
  const [addPress,     setAddPress]     = useState(false);
  // Session-level: which entries have been opened in a new tab.
  const [viewedInTab,  setViewedInTab]  = useState<Set<string>>(new Set());
  // Session-level: which entries skipped the tab-view via HandleHelpModal.
  const [skipViewFor,  setSkipViewFor]  = useState<Set<string>>(new Set());
  const [helpEntry,    setHelpEntry]    = useState<{ entry: PresignupHandle; idx: number } | null>(null);

  const inputRef     = useRef<HTMLInputElement>(null);
  const cardRef      = useRef<HTMLDivElement | null>(null);
  const lastEntryRef = useRef<HTMLLIElement | null>(null);
  const prevLengthRef = useRef<number>(entries.length);

  const hasEntries = entries.length > 0;
  const used = entries.length;
  const full = used >= CAP;

  function isInputValid(): boolean {
    if (!handleInput.trim() || !platform) return false;
    return normalizeSocialHandle(handleInput).length > 0;
  }
  const canAdd = !full && !!platform && isInputValid();

  useEffect(() => {
    onHandleCountChange?.(entries.length);
  }, [entries.length, onHandleCountChange]);

  useEffect(() => {
    // Fire initial confirmed count so the parent gate is correct on mount.
    onConfirmedHandleCountChange?.(entries.filter((e) => e.confirmed).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to newest entry on list growth.
  useEffect(() => {
    if (entries.length > prevLengthRef.current) {
      setTimeout(() => {
        if (!lastEntryRef.current) return;
        const bandEl = document.querySelector(".sticky-header") as HTMLElement | null;
        const bandHeight = bandEl ? bandEl.offsetHeight : 0;
        lastEntryRef.current.style.scrollMarginTop = `${bandHeight}px`;
        lastEntryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
    }
    prevLengthRef.current = entries.length;
  }, [entries.length]);

  function openExpanded() {
    setPlatform(null);
    setHandleInput("");
    setError(null);
    setExpanded(true);
    setTimeout(() => {
      if (!cardRef.current) return;
      const bandEl = document.querySelector(".sticky-header") as HTMLElement | null;
      const bandHeight = bandEl ? bandEl.offsetHeight : 0;
      cardRef.current.style.scrollMarginTop = `${bandHeight}px`;
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  function handlePlatformChange(id: string) {
    setPlatform(id);
    setError(null);
    setTimeout(() => inputRef.current?.focus(), 40);
  }

  function handleAdd() {
    setError(null);
    if (!platform) return;
    const raw = handleInput.trim();
    if (!raw) return;

    const normalized = normalizeSocialHandle(raw);
    if (!normalized) {
      setError("Enter a valid username or profile URL.");
      return;
    }

    if (entries.some((e) => e.platform === platform && e.handle === normalized)) {
      setError("Already in your list.");
      return;
    }

    if (full) {
      setError("List full — remove one to add another.");
      return;
    }

    const next: PresignupHandle[] = [
      ...entries,
      { platform, handle: normalized, addedAt: Date.now(), confirmed: false },
    ];
    setEntries(next);
    writePresignupHandleStore(next);
    setHandleInput("");
    setExpanded(false);
  }

  function handleRemove(idx: number) {
    const removed = entries[idx];
    const k = entryKey(removed);
    const next = entries.filter((_, i) => i !== idx);
    setEntries(next);
    writePresignupHandleStore(next);
    onConfirmedHandleCountChange?.(next.filter((e) => e.confirmed).length);
    setViewedInTab((prev) => { const s = new Set(prev); s.delete(k); return s; });
    setSkipViewFor((prev) => { const s = new Set(prev); s.delete(k); return s; });
  }

  function handleOpenTab(entry: PresignupHandle) {
    const url = profileUrl(entry.platform, entry.handle);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    setViewedInTab((prev) => new Set(prev).add(entryKey(entry)));
  }

  function handleConfirmEntry(idx: number) {
    const next = entries.map((e, i) => i === idx ? { ...e, confirmed: true } : e);
    setEntries(next);
    writePresignupHandleStore(next);
    onConfirmedHandleCountChange?.(next.filter((e) => e.confirmed).length);
  }

  // ── Styles ─────────────────────────────────────────────────────────────────

  const confirmBtn: React.CSSProperties = {
    width: "100%",
    padding: "10px 20px",
    background: BRAND.coral,
    color: BRAND.white,
    fontFamily: "'Fredoka', sans-serif",
    fontWeight: 700,
    fontSize: 16,
    border: `3px solid ${BRAND.ink}`,
    borderRadius: 100,
    boxShadow: `4px 4px 0 ${BRAND.ink}`,
    cursor: "pointer",
    transition: "all 0.1s ease",
    boxSizing: "border-box",
  };

  const viewBtn: React.CSSProperties = { ...confirmBtn };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      ref={cardRef}
      style={{
        background: "#E8D4B8",
        border: `3px solid ${BRAND.ink}`,
        borderRadius: 20,
        boxShadow: `6px 6px 0px ${BRAND.ink}`,
        padding: "20px 18px 24px",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      {/* Title + subtitle */}
      <div
        style={{
          fontFamily: "'Fredoka', sans-serif",
          fontSize: 24,
          fontWeight: 600,
          color: BRAND.ink,
          textAlign: "center",
          marginBottom: 4,
        }}
      >
        Add Your Handle
      </div>


      {/* Counter — visible from first handle onward */}
      {used > 0 && (
        <div
          style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: 12,
            fontWeight: 700,
            color: "rgba(26,26,26,0.5)",
            textAlign: "center",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            marginBottom: 16,
          }}
        >
          {used} of {CAP}
        </div>
      )}

      {/* Collapsed bar */}
      {!expanded && (
        <div
          role={full ? undefined : "button"}
          tabIndex={full ? -1 : 0}
          onClick={full ? undefined : openExpanded}
          onKeyDown={full ? undefined : (e) => e.key === "Enter" && openExpanded()}
          style={{
            display: "flex",
            alignItems: "center",
            border: `2px solid ${BRAND.coral}`,
            borderRadius: 100,
            padding: "13px 20px",
            background: "#FFFFFF",
            cursor: full ? "not-allowed" : "text",
            fontFamily: "'Nunito', sans-serif",
            fontSize: 15,
            color: "rgba(26,26,26,0.4)",
            userSelect: "none",
            opacity: full ? 0.45 : 1,
          }}
        >
          {full ? "List Full" : hasEntries ? "Add another handle" : "Add your handle"}
        </div>
      )}

      {/* HandleHelpModal — "Having trouble?" skip path */}
      {helpEntry && (
        <HandleHelpModal
          platform={helpEntry.entry.platform}
          handle={helpEntry.entry.handle}
          onConfirmed={() => {
            const currentIdx = entries.findIndex(
              (en) => en.platform === helpEntry.entry.platform && en.handle === helpEntry.entry.handle,
            );
            setHelpEntry(null);
            if (currentIdx !== -1) handleConfirmEntry(currentIdx);
          }}
          onReEnter={() => setHelpEntry(null)}
          onDismiss={() => setHelpEntry(null)}
        />
      )}

      {/* Expanded form */}
      <div
        style={{
          overflow: "hidden",
          maxHeight: expanded ? "480px" : "0px",
          opacity: expanded ? 1 : 0,
          transition: "max-height 0.3s ease, opacity 0.25s ease",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingRight: 6, paddingBottom: 6 }}>

          {/* Platform picker — 4 social platforms, no phone/email */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            {SOCIAL_PLATFORMS.map(({ id, label, Icon }) => {
              const active = platform === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handlePlatformChange(id)}
                  title={label}
                  aria-label={label}
                  aria-pressed={active}
                  className={`relative flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-all ${
                    active
                      ? "bg-brand text-white shadow-[2px_2px_0px_#D4724A]"
                      : "bg-[#ffffff] text-[#C96B6B] hover:bg-[#D4B896] focus:outline-none focus:ring-0 active:outline-none outline-none shadow-[2px_2px_0px_#D4724A]"
                  }`}
                >
                  <Icon size={20} />
                </button>
              );
            })}
          </div>

          {/* Handle input + Add button */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              ref={inputRef}
              type="text"
              autoComplete="off"
              placeholder={
                platform === null        ? "Pick a platform above" :
                platform === "instagram" ? "username or instagram.com/username" :
                platform === "tiktok"    ? "username or tiktok.com/@username" :
                platform === "snapchat"  ? "username or snapchat.com/add/username" :
                platform === "facebook"  ? "username or facebook.com/username" :
                                           "username"
              }
              value={handleInput}
              onChange={(e) => {
                setError(null);
                setHandleInput(e.target.value.toLowerCase());
              }}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              disabled={full}
              style={{
                width: "100%",
                border: `2px solid ${BRAND.coral}`,
                borderRadius: 12,
                padding: "12px 16px",
                fontFamily: "'Nunito', sans-serif",
                fontSize: 15,
                background: full ? "rgba(26,26,26,0.04)" : "#FFFFFF",
                color: BRAND.ink,
                outline: "none",
                boxSizing: "border-box",
                transition: "border-color 0.1s ease",
              }}
            />

            {error && (
              <div
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 13,
                  color: BRAND.coral,
                  padding: "6px 12px",
                  background: "rgba(224,90,58,0.07)",
                  border: `1.5px solid ${BRAND.coral}`,
                  borderRadius: 8,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={handleAdd}
              disabled={!canAdd}
              onMouseEnter={() => canAdd && setAddHov(true)}
              onMouseLeave={() => { setAddHov(false); setAddPress(false); }}
              onMouseDown={() => canAdd && setAddPress(true)}
              onMouseUp={() => setAddPress(false)}
              style={{
                width: "100%",
                padding: "12px 24px",
                background: addPress ? "#B34428" : addHov ? "#C94E30" : BRAND.coral,
                color: BRAND.white,
                fontFamily: "'Fredoka', sans-serif",
                fontWeight: 700,
                fontSize: 18,
                border: `3px solid ${BRAND.ink}`,
                borderRadius: 100,
                boxShadow: `4px 4px 0 ${BRAND.ink}`,
                cursor: canAdd ? "pointer" : "not-allowed",
                opacity: canAdd ? 1 : 0.45,
                transform: addPress ? "scale(0.97)" : "scale(1)",
                transition: "all 0.1s ease",
                boxSizing: "border-box",
              }}
            >
              {full ? "List Full" : "Add Handle"}
            </button>
          </div>

        </div>
      </div>

      {/* Entry list — stays visible when form is collapsed */}
      {hasEntries && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontFamily: "'Fredoka', sans-serif",
              fontSize: 17,
              fontWeight: 700,
              color: BRAND.ink,
              marginBottom: 10,
            }}
          >
            Your Handles ({used})
          </div>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              borderRadius: 14,
            }}
          >
            {entries.map((e, i) => {
              const k         = entryKey(e);
              const viewed    = viewedInTab.has(k);
              const skipping  = skipViewFor.has(k);
              const canConfirm = viewed || skipping;

              return (
                <li
                  key={i}
                  ref={i === entries.length - 1 ? lastEntryRef : null}
                  style={{
                    background: BRAND.pageBg,
                    
                    borderRadius: 14,
                    padding: "12px 14px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {/* Header row: handle info + remove */}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontFamily: "'Nunito', sans-serif",
                          fontSize: 14,
                          fontWeight: 700,
                          color: BRAND.ink,
                          wordBreak: "break-all",
                        }}
                      >
                        @{e.handle}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 5,
                          marginTop: 5,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            background: BRAND.coral,
                            color: BRAND.white,
                            borderRadius: 100,
                            padding: "2px 10px",
                            fontFamily: "'Fredoka', sans-serif",
                            fontSize: 12,
                            textTransform: "capitalize",
                          }}
                        >
                          {e.platform}
                        </span>
                        {e.confirmed && (
                          <span
                            style={{
                              background: "rgba(26,26,26,0.08)",
                              color: "rgba(26,26,26,0.65)",
                              borderRadius: 100,
                              padding: "2px 10px",
                              fontFamily: "'Fredoka', sans-serif",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            ✓ Confirmed
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemove(i)}
                      style={{
                        background: BRAND.white,
                        color: BRAND.ink,
                        border: `2px solid ${BRAND.ink}`,
                        borderRadius: 100,
                        padding: "4px 12px",
                        fontFamily: "'Fredoka', sans-serif",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        flexShrink: 0,
                        boxShadow: `2px 2px 0 ${BRAND.ink}`,
                        transition: "all 0.1s ease",
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Confirm actions — hidden once confirmed */}
                  {!e.confirmed && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>

                      {/* Step 1: view profile in tab */}
                      {!canConfirm && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleOpenTab(e)}
                            style={viewBtn}
                          >
                            View My Profile
                          </button>
                          <button
                            type="button"
                            onClick={() => setHelpEntry({ entry: e, idx: i })}
                            style={{
                              background: "none",
                              border: "none",
                              fontFamily: "'Nunito', sans-serif",
                              fontSize: 13,
                              fontWeight: 600,
                              color: "rgba(26,26,26,0.45)",
                              cursor: "pointer",
                              textDecoration: "underline",
                              textUnderlineOffset: 3,
                              padding: "4px 0",
                              textAlign: "center",
                            }}
                          >
                            Having trouble viewing your profile?
                          </button>
                        </>
                      )}

                      {/* Step 2: confirm after view (or after "Having trouble?" skip) */}
                      {canConfirm && (
                        <button
                          type="button"
                          onClick={() => handleConfirmEntry(i)}
                          style={confirmBtn}
                        >
                          This Is Me ✓
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
